import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Alert, Form, Input } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';

import { change, ILoginState, IPasswordState } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';

import { ChangePassword } from '../../../common/change-password';

import styles from './styles.module.scss';

// Same minimum the legacy change-password form enforces.
const MIN_PASSWORD_LENGTH = 5;

interface IPasswordFormValues {
  current: string;
  password: string;
  confirm: string;
}

/** Change password in the hub layout; same rules and `change` thunk as the legacy form. */
export const AccountPasswordForm = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const [form] = Form.useForm<IPasswordFormValues>();

  const passwordStore: IPasswordState = useSelector((state: FunctionalRootState) => state.passwordStore);
  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  // AshevillePlay has its own password flow; keep it as is.
  if (loginStore.account?.isAshevillePlay) {
    return <ChangePassword hideTitle />;
  }

  const { isSuccess, message, loading } = passwordStore;

  const handleFinish = (values: IPasswordFormValues) => {
    dispatch(change(values.current, values.password, true));
    form.resetFields();
  };

  return (
    <div className={styles.passwordLayout}>
      <section className={styles.card}>
        <Form form={form} layout="vertical" requiredMark={false} className={styles.hubForm} onFinish={handleFinish}>
          <Form.Item
            name="current"
            label={intl.formatMessage({ id: 'current.password' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'enter.your.password' }) }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="password"
            label={intl.formatMessage({ id: 'new.password' })}
            rules={[
              { required: true, message: intl.formatMessage({ id: 'enter.new.password' }) },
              { min: MIN_PASSWORD_LENGTH, message: intl.formatMessage({ id: 'password.must.be.minimum.characters' }) },
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label={intl.formatMessage({ id: 'confirm.new.password' })}
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: intl.formatMessage({ id: 'please.confirm.your.password' }) },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(intl.formatMessage({ id: 'the.two.passwords.that.you.entered.do.not.match' })));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          {message && !loading ? (
            <Alert className={styles.alert} description={message} type={isSuccess ? 'success' : 'error'} showIcon />
          ) : null}

          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton} disabled={loading}>
              {intl.formatMessage({ id: loading ? 'my.account.hub.password.saving' : 'my.account.hub.password.update' })}
            </button>
          </div>
        </Form>
      </section>

      <aside className={styles.tips} aria-labelledby="account-hub-password-tips">
        <span className={styles.tipsIcon} aria-hidden="true"><FontAwesomeIcon icon={faShieldHalved} /></span>
        <h2 id="account-hub-password-tips" className={styles.tipsTitle}>
          {intl.formatMessage({ id: 'my.account.hub.password.tips_title' })}
        </h2>
        <ul className={styles.tipsList}>
          <li>{intl.formatMessage({ id: 'my.account.hub.password.tip_length' }, { count: MIN_PASSWORD_LENGTH })}</li>
          <li>{intl.formatMessage({ id: 'my.account.hub.password.tip_mix' })}</li>
          <li>{intl.formatMessage({ id: 'my.account.hub.password.tip_unique' })}</li>
        </ul>
      </aside>
    </div>
  );
};
